import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Form, Select } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTaskSlice } from '../../slice';
import {
  IChangedFormField,
  ITaskFilters,
  ITaskFiltersFormValues,
  taskFilterEnum,
  taskFilterFormEnum,
  taskFormValuesEnum,
  TaskListSort,
  taskStatusEnum,
  TaskStatusType,
} from '../../slice/types';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import moment, { Moment } from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
import { getTaskListFiltersSearchQuery } from 'utils/helpers';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import TaskCategorySelect from 'app/components/Select/TaskCategorySelect/TaskCategorySelect';
import { IOption } from 'types';
import { getTaskStatusText } from '../../../../../utils/helpers';
import { selectIsConsultant } from 'app/containers/Authorization/slice/selectors';

interface IProps {
  layout?: 'vertical' | 'horizontal' | 'inline';
  filters: ITaskFilters;
  sort: TaskListSort;
  currentPage: number;
  fieldId?: number;
  setFilters: (filters: ITaskFilters) => void;
  setCurrentPage: (page: number) => void;
}

const TASK_LIST_FILTERS_FORM_ID = 'taskListFiltersForm';

const consultantTaskStatuses: TaskStatusType[] = [
  'RECOMMENDED',
  'PLANNED',
  'IN_PROGRESS',
  'COMPLETED',

  'DECLINED',
];

const notConsultantTaskStatuses: TaskStatusType[] = [
  'RECOMMENDED',
  'PLANNED',
  'IN_PROGRESS',
  'COMPLETED',
];

const TaskListFilters: FC<IProps> = ({
  layout = 'vertical',
  filters,
  sort,
  currentPage,
  fieldId,
  setFilters,
  setCurrentPage,
}) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const { actions } = useTaskSlice();

  const dispatch = useDispatch();

  const location = useLocation();

  const history = useHistory();

  const envId = useSelector(selectSelectedEnvId);
  const selectedCompanyId = useSelector(selectSelectedCompanyId);
  const selectedSeasonId = useSelector(selectSelectedSeasonId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const isConsultant = useSelector(selectIsConsultant);

  const [
    initQueryParamsSetupFinished,
    setInitQueryParamsSetupFinished,
  ] = useState(false);

  const getListData = useCallback(() => {
    if (envId) {
      dispatch(
        actions.getTaskList({
          envId,
          filters,
          sort,
          companyId: selectedCompanyId,
          seasonId: selectedSeasonId,
          fieldId,
          page: currentPage,
          companyGroupId,
        }),
      );
    }
  }, [
    fieldId,
    dispatch,
    actions,
    envId,
    filters,
    sort,
    selectedCompanyId,
    selectedSeasonId,
    currentPage,
    companyGroupId,
  ]);

  useEffect(() => {
    getListData();
  }, [
    fieldId,
    envId,
    filters,
    selectedCompanyId,
    selectedSeasonId,
    getListData,
    currentPage,
  ]);

  useEffect(() => {
    if (initQueryParamsSetupFinished) {
      const query = getTaskListFiltersSearchQuery({
        filters,
        locationSearch: location.search,
        currentPage,
      });
      history.push({ search: query });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, location.search, currentPage]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const filtersObj: ITaskFilters = {
      startDate: undefined,
      endDate: undefined,
      categoryId: undefined,
      status: undefined,
    };

    Object.values(taskFilterEnum).forEach(taskFilterName => {
      const filterValue = query.get(taskFilterName);

      if (filterValue) {
        if (taskFilterEnum.status === taskFilterName) {
          const status = filterValue
            .toUpperCase()
            .split(',') as TaskStatusType[];
          filtersObj.status = status;
        } else {
          filtersObj[taskFilterName] = filterValue;
        }
      }
    });
    setFilters(filtersObj);

    const pageInFilter = query.get('page');
    if (pageInFilter && Number(pageInFilter) !== currentPage) {
      setCurrentPage(Number(pageInFilter));
    }

    setInitQueryParamsSetupFinished(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const formValues: ITaskFiltersFormValues = form.getFieldsValue();

    for (const keyValueTuple of Object.entries(filters)) {
      const [key, value] = keyValueTuple as [
        taskFilterEnum,
        taskStatusEnum | string | undefined,
      ];
      if (key === taskFilterEnum.startDate || key === taskFilterEnum.endDate) {
        const formDateMoments = formValues[taskFormValuesEnum.date] || [];
        const formStartDateMoment = formDateMoments[0];
        const formEndDateMoment = formDateMoments[1];
        if (key === taskFilterEnum.startDate) {
          const filterStartDate = value;
          const formStartDateString = formStartDateMoment?.format('YYYY-MM-DD');

          const isNotEqualDates = formStartDateString !== filterStartDate;
          if (isNotEqualDates) {
            form.setFieldsValue({
              [taskFilterFormEnum.date]: [
                filterStartDate ? moment(filterStartDate) : undefined,
                formEndDateMoment,
              ],
            });
          }
        } else {
          const filterEndDate = value;
          const formEndDateString = formEndDateMoment?.format('YYYY-MM-DD');

          const isNotEqualDates = formEndDateString !== filterEndDate;
          if (isNotEqualDates) {
            form.setFieldsValue({
              [taskFilterFormEnum.date]: [
                formStartDateMoment,
                filterEndDate ? moment(filterEndDate) : undefined,
              ],
            });
          }
        }
      } else if (formValues[key] !== value) {
        if (key === taskFilterEnum.categoryId) {
          form.setFieldsValue({ [key]: Number(value) || value });
        } else {
          form.setFieldsValue({ [key]: value });
        }
      }
    }
  }, [filters, form]);

  const statusOptions: IOption<TaskStatusType>[] = (isConsultant
    ? consultantTaskStatuses
    : notConsultantTaskStatuses
  ).map(status => ({
    label: getTaskStatusText(status),
    value: status,
  }));

  const onFormChange = (
    values: IChangedFormField<TaskStatusType[] | null | Moment[]>[],
  ) => {
    if (!values.length) {
      return;
    }

    const changeObj = values[0];
    const name = (changeObj.name as taskFilterFormEnum[])[0];
    const value = changeObj.value;

    if (name === taskFilterFormEnum.date) {
      let startDate = undefined;
      let endDate = undefined;

      if (Array.isArray(value)) {
        startDate = (value[0] as Moment | undefined)?.format('YYYY-MM-DD');
        endDate = (value[1] as Moment | undefined)?.format('YYYY-MM-DD');
      }

      setFilters({
        startDate,
        endDate,
      });
      return;
    }

    setFilters({ [name]: value });
  };

  return (
    <Form
      layout={layout}
      form={form}
      initialValues={{ layout: layout }}
      requiredMark="optional"
      id={TASK_LIST_FILTERS_FORM_ID}
      onFieldsChange={onFormChange}
    >
      <Form.Item name={taskFilterFormEnum.date}>
        <DatePicker.RangePicker
          style={{ width: '100%' }}
          allowClear
          allowEmpty={[true, true]}
        />
      </Form.Item>
      <Form.Item name={taskFilterFormEnum.categoryId}>
        <TaskCategorySelect
          placeholder={t('Task.List.TaskCategory', 'Task category')}
          allowClear
          dropdownMatchSelectWidth={false}
          getPopupContainer={trigger => trigger.parentNode}
        />
      </Form.Item>
      <Form.Item name={taskFilterFormEnum.status}>
        <Select
          style={{ minWidth: 150 }}
          dropdownMatchSelectWidth={false}
          placeholder={t('Task.List.TaskStatus', 'Task status')}
          allowClear
          getPopupContainer={trigger => trigger.parentNode}
          options={statusOptions}
          mode="multiple"
        />
      </Form.Item>
    </Form>
  );
};

export default TaskListFilters;
